import { cn } from "@mabi-ui/utils";
import { Button, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useBetterMediaQuery } from "../hooks/useBetterMediaQuery";
import { FeatureAnnouncement, useFeatureAnnouncement } from "../hooks/useFeatureAnnouncement";
import CertiblokIcon from "./icons/CertiblokIcon";
import { ModalNavigator, useModalNavigator, useModalRoute } from "./ModalNavigator/ModalNavigator";
import WhiteModal from "./WhiteModal";

const FeatureAnnouncementModalPusher = () => {
	const { pushModal } = useModalNavigator();
	const { shouldShowAnnouncement, markAsViewed, latestFeature } = useFeatureAnnouncement();
	const isModalOpenRef = useRef(false);

	useEffect(() => {
		if (!isModalOpenRef.current && shouldShowAnnouncement && latestFeature) {
			isModalOpenRef.current = true;
			pushModal(FeatureAnnouncementModal, { feature: latestFeature }).then(() => {
				markAsViewed();
				isModalOpenRef.current = false;
			});
		}
	}, [shouldShowAnnouncement, markAsViewed, pushModal, latestFeature]);

	return null;
};

const FeatureAnnouncementModal = ModalNavigator.create(
	({ feature, onSuccess }: { feature: FeatureAnnouncement; onSuccess?: () => void }) => {
		const { t } = useTranslation();
		const { visible } = useModalRoute();
		const { popModal } = useModalNavigator();
		const isMd = useBetterMediaQuery("md");

		const onClose = () => {
			popModal();
			onSuccess?.();
		};

		return (
			<WhiteModal
				open={visible}
				onClose={onClose}
				responsive
				containerSx={{
					width: {
						xs: "90vw",
						sm: 550,
						lg: 550,
					},
				}}
			>
				<div>
					<div className="mb-6 md:w-16 w-12 md:h-16 h-12 w flex items-center justify-center rounded-full bg-alert/[0.12]">
						<CertiblokIcon name="comunication_bold_01" color="#FFD21F" size={isMd ? 40 : 32} />
					</div>
					<div className="mb-6">
						<h6 className="text-h6 font-bold md:text-h4 md:font-normal">
							<Trans components={{ strong: <strong className="font-bold" /> }}>{feature.title}</Trans>
						</h6>
						<Typography className="mt-2 text-black/[0.6]">
							<Trans
								components={{
									strong: <strong className="font-bold" />,
									list: <ul className="list-disc pl-6 list-outside" />,
									listitem: <li className="list-item mt-1" />,
								}}
							>
								{feature.description}
							</Trans>
						</Typography>
					</div>
					<div className={cn("flex items-center gap-4", feature.actionUrl ? "justify-between" : "justify-end")}>
						<Button
							variant="outlined"
							color="secondary"
							startIcon={<CertiblokIcon name="close" color="secondary" />}
							sx={{ typography: isMd ? "button" : undefined }}
							size={isMd ? "large" : "medium"}
							onClick={onClose}
						>
							{t("global.close")}
						</Button>
						{feature.actionUrl && (
							<Button
								variant="contained"
								color="secondary"
								href={feature.actionUrl}
								sx={{ typography: isMd ? "button" : undefined }}
								size={isMd ? "large" : "medium"}
								onClick={onClose}
							>
								{feature.actionText || t("global.exploreDetails")}
							</Button>
						)}
					</div>
				</div>
			</WhiteModal>
		);
	}
);

export default FeatureAnnouncementModalPusher;
